import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FORM } from 'src/constants/Form';
import { TextField, Tooltip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { t } from 'src/lib/language';
import { useEffect, useState } from 'react';
import {
  getContactBankAccountAction,
  getContactCreditCardAction,
} from 'src/redux/actions/paymentInfoAction';
import { size } from 'lodash';
import { PAYMENT_TYPE } from 'src/models/PaymentModel';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { CreditCardModel } from 'src/models/CreditCardModel';

interface Props {
  contactId: number,
  form: FORM,
  gatewayId: number,
  name: string,
  margin?: 'dense' | 'normal' | undefined,
  noMarginTop?: boolean
  onChange: (id: number) => void,
  style?: CSSProperties,
  type: PAYMENT_TYPE,
  value?: number,
}

interface DataItem {
  id: number,
  label?: string,
  description?: string,
}

const PaymentAccountSelector = (props: Props) => {
  const dispatch = useAppDispatch();

  const {
    contactId,
    form,
    gatewayId,
    margin,
    name,
    onChange,
    style,
    type,
    value,
  } = props;

  const data = useSelector((state: Store) => {
    if (PAYMENT_TYPE.eft === type) {
      return state.contactBankAccounts[contactId];
    }
    if (!state.contactCreditcards[contactId]) {
      return state.contactCreditcards[contactId];
    }
    if (gatewayId) {
      return state.contactCreditcards[contactId].filter(
        (card: CreditCardModel) => card.gatewayId === gatewayId,
      );
    }
    return state.contactCreditcards[contactId];
  });
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const errors: ErrorBag =
    useSelector((state: Store) => state.errors[form] || {} as ErrorBag);
  const [getLabelState, setLabelState] = useState(PAYMENT_TYPE.eft);

  useEffect(() => {
    switch (type) {
      case PAYMENT_TYPE.cc:
      case PAYMENT_TYPE.wallet:
        dispatch(getContactCreditCardAction(contactId));
        setLabelState(PAYMENT_TYPE.cc);
        break;
      default:
        dispatch(getContactBankAccountAction(contactId));
        setLabelState(PAYMENT_TYPE.eft);
    }
  }, [
    contactId,
    type,
  ]);

  const getLabel = (item: any) => {
    if ((PAYMENT_TYPE.cc === type) || (PAYMENT_TYPE.wallet === type)) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { masked_card_number, expiration_data, is_validated } = item;
      return `${masked_card_number} Exp ${expiration_data} ${is_validated ? '' : '(old token)'}`;
    }
    return `#${item.account}`;
  };

  const getItems = () => {
    const items: DataItem[] = [{ id: 0 }];
    if (!data || !size(data)) {
      return items;
    }

    const accounts = data ?? [];

    accounts.forEach((item: any) => {
      items.push({
        id: item.id,
        label: getLabel(item),
        description: item.name,
      } as DataItem);
    });

    return items;
  };

  const onValueChange = (item: DataItem) => {
    if (!item) {
      onChange(0);
      return null;
    }
    onChange(item.id as number);
    return null;
  };

  const getValue = (initialValue?: number) => {
    let result = {
      id: 0,
      label: undefined,
    } as DataItem;
    if (!data || !size(data) || (!contactId)) {
      return result;
    }

    const accounts = data ?? [];

    accounts.forEach((item: any) => {
      if (item.id === initialValue) {
        result = {
          id: item.id,
          label: ((PAYMENT_TYPE.cc === type) || (PAYMENT_TYPE.wallet === type))
            ? `${item.masked_card_number} Exp ${item.expiration_data}`
            : `#${item.account}`,
        } as DataItem;
      }
    });
    return result;
  };

  const renderLabel = (option: DataItem) => {
    if (option.label) {
      return (
        <Tooltip
          title={option.description as string}
          placement="top-start">
          <span>{option.label}</span>
        </Tooltip>
      );
    }
    return (<></>);
  };

  const getOptionLabel = (option: DataItem) => option.label || '';

  return (
    <Autocomplete
      style={style}
      value={getValue(value)}
      onChange={(e, item) => onValueChange(item as DataItem)}
      options={getItems() || []}
      getOptionLabel={(option: DataItem) => getOptionLabel(option)}
      getOptionSelected={(option, item) => option.id === item.id}
      renderOption={(option: DataItem) => renderLabel(option)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={t(lang, `forms.payments.contact_${getLabelState}`)}
          placeholder={t(lang, `forms.payments.contact_${getLabelState}`)}
          error={!!errors[name]}
          margin={margin || 'normal'} />
      )} />
  );
};

export default PaymentAccountSelector;
